/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { ContactMetadata, ContactRelationship } from '../../@types/contact';
import type { XHubChatClient } from '../../client';
import type { Room } from '../room/room';
import type { User } from './user';
import { UserEvent } from '../../@types/user';
import { TypedEventEmitter } from '../../emit/typed-event-emitter';
import { logger } from '../../utils/logger';

/**
 * Events emitted by Contact
 */
export enum ContactEvent {
  /** Emitted when contact data is updated */
  Update = 'Contact.update',
  /** Emitted when contact's presence changes */
  PresenceChange = 'Contact.presenceChange',
  /** Emitted when a direct room is created */
  DirectRoomCreated = 'Contact.directRoomCreated',
}

export type ContactEventHandlerMap = {
  [ContactEvent.Update]: (contact: Contact) => void;
  [ContactEvent.PresenceChange]: (presence: string, previousPresence: string) => void;
  [ContactEvent.DirectRoomCreated]: (room: Room) => void;
};

/**
 * Contact represents a user in the contacts list.
 * It wraps a User object and adds contact-specific metadata.
 *
 * @example
 * ```typescript
 * const contact = new Contact(userId, user, ContactRelationship.Added, {}, client);
 *
 * // Get display info
 * console.log(contact.getDisplayName());
 * console.log(contact.isOnline());
 *
 * // Create DM
 * const room = await contact.getOrCreateDirectRoom();
 * await client.sendTextMessage(room.roomId, 'Hello!');
 *
 * // Listen to presence changes
 * contact.on(ContactEvent.PresenceChange, (presence) => {
 *   console.log(`Contact is now ${presence}`);
 * });
 * ```
 */
export class Contact extends TypedEventEmitter<ContactEvent, ContactEventHandlerMap> {
  private directRoom?: Room;
  private previousPresence: string;

  /**
   * Construct a new Contact.
   *
   * @param userId - The user ID of the contact
   * @param user - The User object for this contact
   * @param relationship - The relationship type
   * @param metadata - Additional metadata about the contact
   * @param client - The XHubChat client instance
   * @param directRoomId - Optional existing direct room ID
   * @param addedAt - Timestamp when contact was added
   * @param lastInteraction - Timestamp of last interaction
   */
  public constructor(
    public readonly userId: string,
    public readonly user: User,
    public relationship: ContactRelationship,
    public metadata: ContactMetadata = {},
    private readonly client: XHubChatClient,
    public directRoomId?: string,
    public addedAt: number = Date.now(),
    public lastInteraction?: number,
  ) {
    super();

    this.previousPresence = this.user.presence;

    // Listen to user presence changes
    this.user.on(UserEvent.Presence, this.onPresenceChange.bind(this));
    this.user.on(UserEvent.DisplayName, this.onUserUpdate.bind(this));
    this.user.on(UserEvent.AvatarUrl, this.onUserUpdate.bind(this));

    // If we have a direct room ID, try to get the room
    if (this.directRoomId) {
      const room = this.client.getRoom(this.directRoomId);
      if (room) {
        this.directRoom = room;
      }
    }
  }

  /**
   * Get channel slug associated with the contact
   */
  get channel_slug(): string | undefined {
    return this.user?.channel_slug;
  }

  /**
   * Get user description
   */
  get description(): string | undefined {
    return this.user?.description;
  }

  /**
   * Handle user presence changes
   */
  private onPresenceChange(): void {
    const currentPresence = this.user.presence;
    if (currentPresence !== this.previousPresence) {
      logger.debug(`Contact ${this.userId} presence changed: ${this.previousPresence} -> ${currentPresence}`);
      this.emit(ContactEvent.PresenceChange, currentPresence, this.previousPresence);
      this.previousPresence = currentPresence;
    }
  }

  /**
   * Handle user info updates
   */
  private onUserUpdate(): void {
    this.emit(ContactEvent.Update, this);
  }

  /**
   * Get display name (fallback to userId if not set)
   */
  get displayName(): string | undefined {
    return this.user.displayName;
  }

  /**
   * Get raw display name (fallback to userId if not set)
   */
  get rawDisplayName(): string | undefined {
    return this.user.rawDisplayName;
  }

  /**
   * Get avatar URL
   */
  get avatarUrl(): string | undefined {
    return this.user.avatarUrl;
  }

  /**
   * Get presence state
   */
  public getPresence(): string {
    return this.user.presence;
  }

  /**
   * Get the underlying User object
   */
  public getUser(): User {
    return this.user;
  }

  /**
   * Check if contact is online
   */
  public isOnline(): boolean {
    return this.user.presence === 'online' || this.user.currentlyActive;
  }

  /**
   * Get last active timestamp
   */
  public getLastActiveTs(): number {
    return this.user.getLastActiveTs();
  }

  /**
   * Check if contact is recently active (within last 10 minutes)
   */
  public isRecentlyActive(): boolean {
    if (this.isOnline()) return true;
    const lastActive = this.getLastActiveTs();
    if (!lastActive) return false;
    const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
    return lastActive > tenMinutesAgo;
  }

  /**
   * Get or create direct message room with this contact
   */
  public async getOrCreateDirectRoom(): Promise<Room> {
    // Return cached room if available
    if (this.directRoom) {
      logger.debug(`Using cached direct room ${this.directRoom.roomId} for contact ${this.userId}`);
      return this.directRoom;
    }

    // Try to find existing room by ID
    if (this.directRoomId) {
      const room = this.client.getRoom(this.directRoomId);
      if (room) {
        logger.debug(`Found existing direct room ${this.directRoomId} for contact ${this.channel_slug}`);
        this.directRoom = room;
        return room;
      }
    }

    // Create new DM room
    try {
      const room = await this.client.createDirectRoom(this.channel_slug || this.userId);
      this.directRoom = room;
      this.directRoomId = room.roomId;
      this.emit(ContactEvent.DirectRoomCreated, room);
      this.emit(ContactEvent.Update, this);
      return room;
    } catch (error) {
      logger.error(`Failed to create direct room with contact ${this.userId}`, error);
      throw error;
    }
  }

  /**
   * Update contact relationship
   */
  public updateRelationship(relationship: ContactRelationship): void {
    if (this.relationship !== relationship) {
      logger.debug(`Contact ${this.userId} relationship changed: ${this.relationship} -> ${relationship}`);
      this.relationship = relationship;
      this.emit(ContactEvent.Update, this);
    }
  }

  /**
   * Update contact metadata
   */
  public updateMetadata(metadata: Partial<ContactMetadata>): void {
    this.metadata = { ...this.metadata, ...metadata };
    logger.debug(`Contact ${this.userId} metadata updated`, this.metadata);
    this.emit(ContactEvent.Update, this);
  }

  /**
   * Add tags to contact
   */
  public addTags(...tags: string[]): void {
    const currentTags = this.metadata.tags || [];
    const newTags = [...new Set([...currentTags, ...tags])];
    this.updateMetadata({ tags: newTags });
  }

  /**
   * Remove tags from contact
   */
  public removeTags(...tags: string[]): void {
    const currentTags = this.metadata.tags || [];
    const newTags = currentTags.filter(tag => !tags.includes(tag));
    this.updateMetadata({ tags: newTags });
  }

  /**
   * Check if contact has a specific tag
   */
  public hasTag(tag: string): boolean {
    return this.metadata.tags?.includes(tag) || false;
  }

  /**
   * Update last interaction timestamp
   */
  public updateLastInteraction(timestamp: number = Date.now()): void {
    this.lastInteraction = timestamp;
    logger.debug(`Contact ${this.userId} last interaction updated to ${timestamp}`);
    this.emit(ContactEvent.Update, this);
  }

  /**
   * Get time since last interaction (in milliseconds)
   */
  public getTimeSinceLastInteraction(): number | null {
    if (!this.lastInteraction) return null;
    return Date.now() - this.lastInteraction;
  }

  /**
   * Cleanup resources
   */
  public destroy(): void {
    this.user.off(UserEvent.Presence, this.onPresenceChange.bind(this));
    this.user.off(UserEvent.DisplayName, this.onUserUpdate.bind(this));
    this.user.off(UserEvent.AvatarUrl, this.onUserUpdate.bind(this));
    this.removeAllListeners();
  }
}
